import { t } from '@lingui/core/macro';
import {
  ActionIcon,
  Affix,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  Group,
  List,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip
} from '@mantine/core';
import {
  IconArrowsDiagonal,
  IconArrowsDiagonalMinimize2,
  IconCheck,
  IconFile,
  IconMessageChatbot,
  IconPaperclip,
  IconSend,
  IconTool,
  IconTrash,
  IconX
} from '@tabler/icons-react';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { api } from '../../App';
import { useUserState } from '../../states/UserState';

// LLM round-trips with tool use frequently take longer than the global
// 5s axios default. Two minutes is generous enough for multi-tool
// reasoning while still bounded; uploads share the same ceiling since
// large file forwards can be slow.
const CHATBOT_REQUEST_TIMEOUT_MS = 2 * 60 * 1000;

type TextBlock = { type: 'text'; text: string };
type ToolUseBlock = {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
};
type ToolResultBlock = {
  type: 'tool_result';
  tool_use_id: string;
  content: string;
  is_error?: boolean;
};
type ContentBlock = TextBlock | ToolUseBlock | ToolResultBlock;

interface Message {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
}

interface PendingAction {
  token: string;
  tool_use_id: string;
  name: string;
  input: Record<string, unknown>;
  summary: string;
}

interface ChatResponse {
  messages: Message[];
  pending_actions: PendingAction[];
}

interface Attachment {
  token: string;
  filename: string;
  kind: string;
  row_count?: number;
  columns?: string[];
}

interface ChatbotStatus {
  enabled: boolean;
  provider: string;
  configured: boolean;
  has_any_tool: boolean;
}

function isAssistantBlocks(m: Message): m is Message & { content: ContentBlock[] } {
  return m.role === 'assistant' && Array.isArray(m.content);
}

function ChatMarkdown({ source }: { source: string }) {
  return (
    <Box
      className='chatbot-markdown'
      style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.45 }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Text size='sm' style={{ margin: '0 0 6px 0' }}>
              {children}
            </Text>
          ),
          strong: ({ children }) => <Text span fw={600}>{children}</Text>,
          em: ({ children }) => <Text span fs='italic'>{children}</Text>,
          h1: ({ children }) => (
            <Text fw={700} size='md' mt={4} mb={4}>
              {children}
            </Text>
          ),
          h2: ({ children }) => (
            <Text fw={700} size='sm' mt={4} mb={2}>
              {children}
            </Text>
          ),
          h3: ({ children }) => (
            <Text fw={600} size='sm' mt={4} mb={2}>
              {children}
            </Text>
          ),
          h4: ({ children }) => (
            <Text fw={600} size='sm' mt={2} mb={2}>
              {children}
            </Text>
          ),
          ul: ({ children }) => (
            <List size='sm' withPadding spacing={2} mb={4}>
              {children as ReactNode}
            </List>
          ),
          ol: ({ children }) => (
            <List size='sm' type='ordered' withPadding spacing={2} mb={4}>
              {children as ReactNode}
            </List>
          ),
          li: ({ children }) => <List.Item>{children}</List.Item>,
          code: ({ className, children }) => (
            <Code className={className} block={!!className}>
              {children}
            </Code>
          ),
          a: ({ href, children }) => (
            <Anchor href={href} target='_blank' rel='noopener noreferrer'>
              {children}
            </Anchor>
          ),
          blockquote: ({ children }) => (
            <Box
              style={{
                borderLeft: '3px solid var(--mantine-color-gray-4)',
                paddingLeft: 8,
                margin: '4px 0',
                color: 'var(--mantine-color-dimmed)'
              }}
            >
              {children}
            </Box>
          ),
          table: ({ children }) => (
            <Box style={{ overflowX: 'auto', margin: '4px 0' }}>
              <Table withTableBorder withColumnBorders striped fz='xs'>
                {children as ReactNode}
              </Table>
            </Box>
          ),
          hr: () => <Box my={6} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }} />
        }}
      >
        {source}
      </Markdown>
    </Box>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';

  // User text message (string content) — simple bubble.
  if (isUser && typeof message.content === 'string') {
    return (
      <Box style={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
        <Paper p='xs' radius='md' bg='var(--mantine-color-blue-light)'>
          <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Text>
        </Paper>
      </Box>
    );
  }

  // User tool-result turn — don't render (it's just plumbing).
  if (isUser) {
    return null;
  }

  // Assistant turn: render text + tool-use blocks.
  if (!isAssistantBlocks(message)) return null;

  return (
    <Stack gap={4} style={{ alignSelf: 'flex-start', maxWidth: '95%' }}>
      {message.content.map((block, i) => {
        if (block.type === 'text' && block.text.trim()) {
          return (
            <Paper
              key={i}
              p='xs'
              radius='md'
              bg='var(--mantine-color-gray-light)'
            >
              <ChatMarkdown source={block.text} />
            </Paper>
          );
        }
        if (block.type === 'tool_use') {
          return (
            <Group key={i} gap={6} c='dimmed'>
              <IconTool size={12} />
              <Text size='xs' c='dimmed'>
                {t`Used`} <Code>{block.name}</Code>
              </Text>
            </Group>
          );
        }
        return null;
      })}
    </Stack>
  );
}

function PendingActionCard({
  action,
  onDecide,
  disabled
}: {
  action: PendingAction;
  onDecide: (token: string, approved: boolean) => void;
  disabled: boolean;
}) {
  return (
    <Paper
      withBorder
      p='xs'
      radius='md'
      style={{ alignSelf: 'stretch', borderColor: 'var(--mantine-color-yellow-5)' }}
    >
      <Stack gap='xs'>
        <Group gap='xs' wrap='nowrap'>
          <Badge color='yellow' variant='light' size='sm'>
            {t`Confirm`}
          </Badge>
          <Text size='xs' c='dimmed'>
            <Code>{action.name}</Code>
          </Text>
        </Group>
        <Text size='sm'>{action.summary}</Text>
        <Group gap='xs' justify='flex-end'>
          <Button
            size='compact-sm'
            variant='default'
            leftSection={<IconX size={14} />}
            onClick={() => onDecide(action.token, false)}
            disabled={disabled}
          >
            {t`Reject`}
          </Button>
          <Button
            size='compact-sm'
            color='green'
            leftSection={<IconCheck size={14} />}
            onClick={() => onDecide(action.token, true)}
            disabled={disabled}
          >
            {t`Approve`}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [pending, setPending] = useState<PendingAction[]>([]);
  const [stashedDecisions, setStashedDecisions] = useState<
    Array<{ token: string; approved: boolean }>
  >([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ChatbotStatus | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, pending, loading]);

  const isLoggedIn = useUserState((s) => s.is_authed);

  useEffect(() => {
    if (!isLoggedIn) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    api
      .get<ChatbotStatus>('/api/chatbot/status/')
      .then((r) => {
        if (!cancelled) setStatus(r.data);
      })
      .catch(() => {
        if (!cancelled)
          setStatus({
            enabled: false,
            provider: '',
            configured: false,
            has_any_tool: false
          });
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // Hide entirely until we know the user is logged in AND the chatbot is
  // enabled AND this user has at least one tool they're permitted to use —
  // otherwise the widget would render but reject every prompt.
  if (!isLoggedIn) return null;
  if (status && !status.enabled) return null;
  if (status && !status.has_any_tool) return null;

  const notReady = status !== null && (!status.enabled || !status.configured);

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || loading || pending.length > 0)
      return;

    // If user only attached files without a text prompt, emit a default ask.
    const userText = text || t`I've attached a file. Please inspect it.`;
    const next: Message[] = [...messages, { role: 'user', content: userText }];
    const tokens = attachments.map((a) => a.token);
    setMessages(next);
    setInput('');
    setAttachments([]);
    setLoading(true);
    setError(null);

    try {
      const resp = await api.post<ChatResponse>(
        '/api/chatbot/message/',
        {
          messages: next,
          attachments: tokens
        },
        { timeout: CHATBOT_REQUEST_TIMEOUT_MS }
      );
      setMessages(resp.data.messages);
      setPending(resp.data.pending_actions ?? []);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.message ??
        t`Something went wrong talking to the chatbot.`;
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const onFilePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.post<Attachment>('/api/chatbot/upload/', fd, {
        timeout: CHATBOT_REQUEST_TIMEOUT_MS
      });
      setAttachments((prev) => [...prev, resp.data]);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ??
        err?.message ??
        t`File upload failed.`;
      setError(detail);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (token: string) => {
    setAttachments((prev) => prev.filter((a) => a.token !== token));
  };

  const decide = async (token: string, approved: boolean) => {
    if (loading) return;
    // Optimistically remove the card; if there are still others, show a
    // loader and let the user finish deciding before we send.
    const remaining = pending.filter((p) => p.token !== token);
    setPending(remaining);
    if (remaining.length > 0) {
      // Stash decisions on the local state until the last one is in.
      setStashedDecisions((s) => [...s, { token, approved }]);
      return;
    }
    await sendDecisions([...stashedDecisions, { token, approved }]);
  };

  const sendDecisions = async (
    decisions: Array<{ token: string; approved: boolean }>
  ) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post<ChatResponse>(
        '/api/chatbot/confirm/',
        {
          messages,
          decisions
        },
        { timeout: CHATBOT_REQUEST_TIMEOUT_MS }
      );
      setMessages(resp.data.messages);
      setPending(resp.data.pending_actions ?? []);
      setStashedDecisions([]);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.message ??
        t`Failed to apply the action.`;
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clear = () => {
    setMessages([]);
    setPending([]);
    setStashedDecisions([]);
    setAttachments([]);
    setError(null);
  };

  const inputDisabled = loading || pending.length > 0 || notReady;
  const sendDisabled =
    inputDisabled || uploading || (!input.trim() && attachments.length === 0);

  return (
    <Affix
      position={
        fullscreen
          ? { top: 0, left: 0, right: 0, bottom: 0 }
          : { bottom: 96, right: 24 }
      }
      zIndex={150}
    >
      {open ? (
        <Paper
          shadow='md'
          radius={fullscreen ? 0 : 'md'}
          withBorder
          p='xs'
          style={{
            width: fullscreen ? '100vw' : 'min(560px, calc(100vw - 32px))',
            height: fullscreen ? '100vh' : 'min(720px, calc(100vh - 120px))',
            display: 'flex'
          }}
        >
          <Stack gap='xs' style={{ flex: 1, minHeight: 0 }}>
            <Group justify='space-between' wrap='nowrap'>
              <Group gap='xs' wrap='nowrap'>
                <IconMessageChatbot size={20} />
                <Text fw={600}>{t`Assistant`}</Text>
              </Group>
              <Group gap={4} wrap='nowrap'>
                <Tooltip label={t`Clear conversation`} withArrow>
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    onClick={clear}
                    disabled={
                      messages.length === 0 && pending.length === 0 && !error
                    }
                    aria-label={t`Clear conversation`}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label={fullscreen ? t`Exit fullscreen` : t`Fullscreen`}
                  withArrow
                >
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    onClick={() => setFullscreen((f) => !f)}
                    aria-label={
                      fullscreen ? t`Exit fullscreen` : t`Fullscreen`
                    }
                  >
                    {fullscreen ? (
                      <IconArrowsDiagonalMinimize2 size={16} />
                    ) : (
                      <IconArrowsDiagonal size={16} />
                    )}
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t`Close`} withArrow>
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    onClick={() => setOpen(false)}
                    aria-label={t`Close chatbot`}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            <ScrollArea style={{ flex: 1 }} viewportRef={scrollRef} type='auto'>
              <Stack gap='xs' p='xs'>
                {notReady && (
                  <Paper
                    withBorder
                    p='xs'
                    radius='md'
                    bg='var(--mantine-color-yellow-light)'
                  >
                    <Text size='sm'>
                      {status && !status.enabled
                        ? t`The chatbot is disabled. An administrator can enable it under Admin Center → Communication → Chatbot.`
                        : t`Please add an API key in Admin Center → Communication → Chatbot to use the chatbot.`}
                    </Text>
                  </Paper>
                )}
                {!notReady &&
                  messages.length === 0 &&
                  pending.length === 0 &&
                  !error && (
                    <Text c='dimmed' size='sm' ta='center'>
                      {t`Ask about parts, stock, orders, or builds — or ask me to update something.`}
                    </Text>
                  )}
                {messages.map((m, i) => (
                  <MessageBubble key={i} message={m} />
                ))}
                {pending.map((a) => (
                  <PendingActionCard
                    key={a.token}
                    action={a}
                    onDecide={decide}
                    disabled={loading}
                  />
                ))}
                {loading && (
                  <Group gap='xs'>
                    <Loader size='xs' />
                    <Text size='xs' c='dimmed'>
                      {t`Thinking…`}
                    </Text>
                  </Group>
                )}
                {error && (
                  <Text size='xs' c='red'>
                    {error}
                  </Text>
                )}
              </Stack>
            </ScrollArea>

            {attachments.length > 0 && (
              <Group gap={4} wrap='wrap'>
                {attachments.map((a) => (
                  <Badge
                    key={a.token}
                    variant='light'
                    color='blue'
                    leftSection={<IconFile size={12} />}
                    rightSection={
                      <ActionIcon
                        size={14}
                        variant='transparent'
                        onClick={() => removeAttachment(a.token)}
                        aria-label={t`Remove attachment`}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    }
                    style={{ paddingRight: 4 }}
                  >
                    {a.filename}
                    {a.row_count != null ? ` · ${a.row_count} rows` : ''}
                  </Badge>
                ))}
              </Group>
            )}

            <Group gap='xs' wrap='nowrap' align='flex-end'>
              <input
                ref={fileInputRef}
                type='file'
                style={{ display: 'none' }}
                onChange={onFilePick}
                accept='.csv,.tsv,.xlsx,.xls,.ods,.json,.txt'
              />
              <Tooltip label={t`Attach file`} withArrow>
                <ActionIcon
                  size='lg'
                  variant='subtle'
                  onClick={() => fileInputRef.current?.click()}
                  disabled={inputDisabled || uploading}
                  aria-label={t`Attach file`}
                >
                  {uploading ? (
                    <Loader size='xs' />
                  ) : (
                    <IconPaperclip size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
              <Textarea
                placeholder={
                  pending.length > 0
                    ? t`Approve or reject the pending action above…`
                    : t`Type a message or attach a file…`
                }
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                autosize
                minRows={1}
                maxRows={4}
                style={{ flex: 1 }}
                disabled={inputDisabled}
              />
              <ActionIcon
                size='lg'
                variant='filled'
                onClick={sendMessage}
                disabled={sendDisabled}
                aria-label={t`Send message`}
              >
                <IconSend size={16} />
              </ActionIcon>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Tooltip label={t`Open chatbot`} position='left' withArrow>
          <ActionIcon
            size={56}
            radius='xl'
            variant='filled'
            onClick={() => setOpen(true)}
            aria-label={t`Open chatbot`}
            style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
          >
            <IconMessageChatbot size={28} />
          </ActionIcon>
        </Tooltip>
      )}
    </Affix>
  );
}
